import { useState, useEffect } from "react";
import { useNavigate, useParams, useOutletContext } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { 
  Package, Truck, CheckCircle, Clock, XCircle, 
  ArrowLeft, Phone, Mail, MapPin, User, History, Loader2, Send
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_FLOW = ["confirmed", "processing", "dispatched", "delivered"];

export default function OpsOrderDetailPage() {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const { user } = useOutletContext() || {};
  const [order, setOrder] = useState(null);
  const [franchisee, setFranchisee] = useState(null);
  const [statusHistory, setStatusHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  
  // Dispatch dialog state
  const [showDispatchDialog, setShowDispatchDialog] = useState(false);
  const [availableOutlets, setAvailableOutlets] = useState([]);
  const [dispatchForm, setDispatchForm] = useState({
    outlet_id: "",
    vehicle_number: "",
    driver_name: "",
    num_persons: 1,
    transport_medium: "",
    notes: ""
  });
  const [isCreatingDispatch, setIsCreatingDispatch] = useState(false);

  useEffect(() => {
    fetchOrderDetails();
  }, [orderId]);

  const fetchOrderDetails = async () => {
    try {
      const [detailRes, historyRes] = await Promise.all([
        axios.get(`${API}/ops/orders/${orderId}`),
        axios.get(`${API}/ops/orders/${orderId}/history`)
      ]);
      setOrder(detailRes.data.order);
      setFranchisee(detailRes.data.franchisee);
      setStatusHistory(historyRes.data);
    } catch (error) {
      console.error("Error fetching order:", error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate("/ops/login");
      } else if (error.response?.status === 404) {
        toast.error("Order not found");
        navigate("/ops/orders");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDispatchInfo = async () => {
    if (!user?.user_id) return;
    try {
      const res = await axios.get(`${API}/ops-extended/order/${orderId}/dispatch-info?ops_manager_id=${user.user_id}`);
      setAvailableOutlets(res.data.available_outlets || []);
    } catch (error) {
      console.error("Error fetching dispatch info:", error);
      toast.error("Failed to load dispatch options");
    }
  };

  const handleStatusUpdate = async () => {
    if (!selectedStatus) return;
    
    setIsUpdating(true);
    try {
      await axios.put(`${API}/ops/orders/${orderId}/status`, {
        status: selectedStatus
      });
      toast.success(`Order marked as ${selectedStatus}`);
      setShowStatusDialog(false);
      fetchOrderDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    } finally {
      setIsUpdating(false);
    }
  };

  const openDispatchDialog = () => {
    fetchDispatchInfo();
    setDispatchForm({
      outlet_id: "",
      vehicle_number: "",
      driver_name: "",
      num_persons: 1,
      transport_medium: "",
      notes: ""
    });
    setShowDispatchDialog(true);
  };

  const handleCreateDispatch = async () => {
    if (!dispatchForm.outlet_id || !dispatchForm.vehicle_number || !dispatchForm.driver_name || !dispatchForm.transport_medium) {
      toast.error("Please fill all required fields");
      return;
    }

    setIsCreatingDispatch(true);
    try {
      const res = await axios.post(`${API}/ops-extended/order-to-dispatch?ops_manager_id=${user.user_id}`, {
        order_id: orderId,
        ...dispatchForm
      });
      toast.success("Dispatch created successfully!");
      setShowDispatchDialog(false);
      fetchOrderDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create dispatch");
    } finally {
      setIsCreatingDispatch(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      confirmed: "bg-yellow-500",
      processing: "bg-blue-500",
      dispatched: "bg-purple-500",
      delivered: "bg-green-500",
      cancelled: "bg-red-500"
    };
    return colors[status] || "bg-gray-500";
  };

  const getStatusIcon = (status) => {
    const icons = {
      confirmed: <Clock className="w-5 h-5" />,
      processing: <Package className="w-5 h-5" />,
      dispatched: <Truck className="w-5 h-5" />,
      delivered: <CheckCircle className="w-5 h-5" />,
      cancelled: <XCircle className="w-5 h-5" />
    };
    return icons[status] || <Clock className="w-5 h-5" />;
  };

  const getNextStatuses = (currentStatus) => {
    if (currentStatus === "cancelled" || currentStatus === "delivered") return [];
    const currentIndex = STATUS_FLOW.indexOf(currentStatus);
    if (currentIndex === -1) return STATUS_FLOW;
    return [...STATUS_FLOW.slice(currentIndex + 1), "cancelled"];
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  // Check if order can be dispatched
  const canDispatch = order && (order.status === "confirmed" || order.status === "processing");

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p>Order not found</p>
      </div>
    );
  }

  const nextStatuses = getNextStatuses(order.status);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-purple-600 text-white sticky top-0 z-50 shadow-lg">
        <div className="container mx-auto px-4 py-3 flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon" 
            className="text-white hover:bg-white/20"
            onClick={() => navigate("/ops/orders")}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-bold text-lg">Order #{order.order_id.slice(-8)}</h1>
            <p className="text-purple-200 text-xs">{formatDate(order.created_at)}</p>
          </div>
          {canDispatch && (
            <Button 
              size="sm" 
              className="bg-white text-purple-600 hover:bg-purple-50 rounded-full"
              onClick={openDispatchDialog}
              data-testid="create-dispatch-btn"
            >
              <Send className="w-4 h-4 mr-1" />
              Dispatch
            </Button>
          )}
        </div>
      </header>

      <main className="container mx-auto px-4 py-4 max-w-2xl pb-24">
        {/* Status Card */}
        <Card className="border-0 shadow-sm mb-4">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 ${getStatusColor(order.status)} rounded-xl flex items-center justify-center text-white`}>
                  {getStatusIcon(order.status)}
                </div>
                <div>
                  <p className="font-semibold capitalize">{order.status}</p>
                  <p className="text-sm text-muted-foreground">Current Status</p>
                </div>
              </div>
              <p className="text-2xl font-bold">₹{order.total_amount?.toFixed(2)}</p>
            </div>
            
            {/* Show dispatch info if dispatched */}
            {order.dispatch_id && (
              <div className="mt-4 p-3 bg-purple-50 rounded-lg">
                <div className="flex items-center gap-2 text-purple-700">
                  <Truck className="w-4 h-4" />
                  <span className="text-sm font-medium">Dispatch: {order.dispatch_id}</span>
                </div>
                {order.dispatched_at && (
                  <p className="text-xs text-purple-600 mt-1">Dispatched: {formatDate(order.dispatched_at)}</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Franchisee Info */}
        <Card className="border-0 shadow-sm mb-4" data-testid="franchisee-info-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <User className="w-4 h-4 text-purple-600" />
              Franchisee Details
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-2">
            <p className="font-medium">{franchisee?.name || order.user_name}</p>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Mail className="w-4 h-4" />
              {franchisee?.email || order.user_email}
            </div>
            {franchisee?.phone && (
              <div className="flex items-center gap-2 text-sm">
                <Phone className="w-4 h-4 text-muted-foreground" />
                <a href={`tel:${franchisee.phone}`} className="text-purple-600">
                  {franchisee.phone}
                </a>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Delivery Address */}
        {order.delivery_address && (
          <Card className="border-0 shadow-sm mb-4" data-testid="delivery-address-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <MapPin className="w-4 h-4 text-purple-600" />
                Delivery Address
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <p className="font-medium">{order.delivery_address.name}</p>
              <p className="text-sm text-muted-foreground">
                {order.delivery_address.address_line1}
                {order.delivery_address.address_line2 && `, ${order.delivery_address.address_line2}`}
              </p>
              <p className="text-sm text-muted-foreground">
                {order.delivery_address.city}, {order.delivery_address.state} - {order.delivery_address.pincode}
              </p>
              {order.delivery_address.phone && (
                <a href={`tel:${order.delivery_address.phone}`} className="text-sm text-purple-600 flex items-center gap-1 mt-2">
                  <Phone className="w-3 h-3" />
                  {order.delivery_address.phone}
                </a>
              )}
            </CardContent>
          </Card>
        )}

        {/* Order Items */}
        <Card className="border-0 shadow-sm mb-4" data-testid="order-items-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Package className="w-4 h-4 text-purple-600" />
              Order Items ({order.items?.length || 0})
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-3">
              {order.items?.map((item, idx) => (
                <div key={idx} className="flex justify-between items-start pb-3 border-b last:border-0 last:pb-0">
                  <div className="flex-1">
                    <p className="font-medium">{item.name}</p>
                    <p className="text-sm text-muted-foreground">
                      ₹{item.price?.toFixed(2)} × {item.quantity}
                    </p>
                  </div>
                  <p className="font-semibold">
                    ₹{(item.price * item.quantity).toFixed(2)}
                  </p>
                </div>
              ))}
            </div>
            <Separator className="my-4" />
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Subtotal</span>
                <span>₹{order.subtotal?.toFixed(2)}</span>
              </div>
              {order.discount_amount > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Discount</span>
                  <span>-₹{order.discount_amount?.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-lg pt-2 border-t">
                <span>Total</span>
                <span>₹{order.total_amount?.toFixed(2)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Status History */}
        {statusHistory.length > 0 && (
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <History className="w-4 h-4 text-purple-600" />
                Status History
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-3">
                {statusHistory.map((entry, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className={`w-8 h-8 ${getStatusColor(entry.new_status)} rounded-full flex items-center justify-center text-white flex-shrink-0`}>
                      {getStatusIcon(entry.new_status)}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium capitalize">{entry.new_status}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(entry.updated_at)} by {entry.updated_by_name}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Fixed Bottom Action Bar */}
      {nextStatuses.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4">
          <div className="container mx-auto max-w-2xl flex gap-2">
            {canDispatch && (
              <Button
                className="flex-1 rounded-full bg-green-600 hover:bg-green-700"
                onClick={openDispatchDialog}
                data-testid="bottom-dispatch-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                Create Dispatch
              </Button>
            )}
            {nextStatuses.slice(0, canDispatch ? 1 : 2).map((status) => (
              <Button
                key={status}
                className={`flex-1 rounded-full ${
                  status === "cancelled" 
                    ? "bg-red-500 hover:bg-red-600" 
                    : "bg-purple-600 hover:bg-purple-700"
                }`}
                onClick={() => {
                  setSelectedStatus(status);
                  setShowStatusDialog(true);
                }}
                data-testid={`update-status-${status}`}
              >
                {getStatusIcon(status)}
                <span className="ml-2 capitalize">Mark {status}</span>
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Status Update Confirmation Dialog */}
      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Update Order Status</DialogTitle>
          </DialogHeader>
          <div className="py-4 text-center">
            <div className={`w-16 h-16 ${getStatusColor(selectedStatus)} rounded-full flex items-center justify-center text-white mx-auto mb-4`}>
              {getStatusIcon(selectedStatus)}
            </div>
            <p>Mark this order as <strong className="capitalize">{selectedStatus}</strong>?</p>
            <p className="text-sm text-muted-foreground mt-1">Order #{order?.order_id?.slice(-8)}</p>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowStatusDialog(false)} 
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleStatusUpdate} 
              disabled={isUpdating}
              className={`rounded-full ${
                selectedStatus === "cancelled" 
                  ? "bg-red-500 hover:bg-red-600" 
                  : "bg-purple-600 hover:bg-purple-700"
              }`}
              data-testid="confirm-status-update"
            >
              {isUpdating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                "Confirm"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Dispatch Dialog */}
      <Dialog open={showDispatchDialog} onOpenChange={setShowDispatchDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-green-600" />
              Create Dispatch from Order
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* Order Summary */}
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm font-medium">Order #{order?.order_id?.slice(-8)}</p>
              <p className="text-xs text-muted-foreground">{order?.items?.length} items • ₹{order?.total_amount?.toFixed(2)}</p>
              <div className="mt-2 space-y-1">
                {order?.items?.slice(0, 3).map((item, idx) => (
                  <p key={idx} className="text-xs text-muted-foreground">• {item.name} × {item.quantity}</p>
                ))}
                {order?.items?.length > 3 && (
                  <p className="text-xs text-muted-foreground">... and {order.items.length - 3} more</p>
                )}
              </div>
            </div>

            {/* Outlet Selection */}
            <div className="space-y-2">
              <Label>Delivery Outlet *</Label>
              <Select value={dispatchForm.outlet_id} onValueChange={(v) => setDispatchForm({...dispatchForm, outlet_id: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select outlet" />
                </SelectTrigger>
                <SelectContent>
                  {availableOutlets.map((outlet) => (
                    <SelectItem key={outlet.outlet_id} value={outlet.outlet_id}>
                      {outlet.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Transport Details */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Vehicle Number *</Label>
                <Input
                  value={dispatchForm.vehicle_number}
                  onChange={(e) => setDispatchForm({...dispatchForm, vehicle_number: e.target.value})}
                  placeholder="KA-01-AB-1234"
                />
              </div>
              <div className="space-y-2">
                <Label>Driver Name *</Label>
                <Input
                  value={dispatchForm.driver_name}
                  onChange={(e) => setDispatchForm({...dispatchForm, driver_name: e.target.value})}
                  placeholder="Driver name"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Transport Medium *</Label>
                <Select value={dispatchForm.transport_medium} onValueChange={(v) => setDispatchForm({...dispatchForm, transport_medium: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bike">Bike</SelectItem>
                    <SelectItem value="auto">Auto</SelectItem>
                    <SelectItem value="van">Van</SelectItem>
                    <SelectItem value="truck">Truck</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>No. of Persons</Label>
                <Input
                  type="number"
                  min="1"
                  value={dispatchForm.num_persons}
                  onChange={(e) => setDispatchForm({...dispatchForm, num_persons: parseInt(e.target.value) || 1})}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea
                value={dispatchForm.notes}
                onChange={(e) => setDispatchForm({...dispatchForm, notes: e.target.value})}
                placeholder="Any special instructions..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowDispatchDialog(false)} className="rounded-full">
              Cancel
            </Button>
            <Button 
              onClick={handleCreateDispatch} 
              disabled={isCreatingDispatch}
              className="rounded-full bg-green-600 hover:bg-green-700"
              data-testid="confirm-dispatch-btn"
            >
              {isCreatingDispatch ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Create Dispatch
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
